import * as Sentry from "@sentry/react";
import React from "react";
import Swal from "sweetalert2";
import { resumableUploadManager } from "../service/upload.service";
import { EventType, trackEvent } from "../utils/analytics";
import { requestWakeLock } from "../utils/wakeScreen";
import { isIOS } from "./device.utils";
import { determineBestAudioFormat } from "./recording.utils";

export interface AudioContextRefs {
  audioContextRef: React.MutableRefObject<AudioContext | null>;
  destinationRef: React.MutableRefObject<MediaStreamAudioDestinationNode | null>;
  currentAudioSourceRef: React.MutableRefObject<MediaStreamAudioSourceNode | null>;
  displayAudioSourceRef?: React.MutableRefObject<MediaStreamAudioSourceNode | null>;
}

export const createCleanupAudioResources = (refs: AudioContextRefs) => {
  const { audioContextRef, destinationRef, currentAudioSourceRef, displayAudioSourceRef } = refs;

  return () => {
    console.log("[CLEANUP] Cleaning up audio resources");

    // Helper function to stop a source node's MediaStream tracks
    const stopSourceNodeTracks = (
      sourceRef: React.MutableRefObject<MediaStreamAudioSourceNode | null>,
      label: string,
    ) => {
      if (sourceRef.current) {
        const sourceNode = sourceRef.current as any; // MediaStreamAudioSourceNode
        if (sourceNode.mediaStream) {
          console.log(`[CLEANUP] Stopping ${label} MediaStream tracks`);
          sourceNode.mediaStream.getTracks().forEach((track: MediaStreamTrack) => {
            console.log(`[CLEANUP] Stopping track: ${track.kind} - ${track.label}`);
            track.stop();
          });
        }

        sourceRef.current.disconnect();
        sourceRef.current = null;
      }
    };

    // Stop main source (microphone or regular audio input)
    stopSourceNodeTracks(currentAudioSourceRef, "source");

    // Stop display source (for headphones mode)
    if (displayAudioSourceRef) {
      stopSourceNodeTracks(displayAudioSourceRef, "display");
    }

    // Close AudioContext
    if (audioContextRef.current && audioContextRef.current.state !== "closed") {
      audioContextRef.current
        .close()
        .then(() => {
          console.log("[CLEANUP] AudioContext closed successfully");
        })
        .catch((error) => {
          console.error("[CLEANUP] Error closing AudioContext:", error);
        });
      audioContextRef.current = null;
    }

    // Clear destination reference
    destinationRef.current = null;
  };
};

export interface MediaRecorderConfig extends AudioContextRefs {
  // Audio device configuration
  recordingDevice?: {
    deviceId?: string;
    groupId?: string;
    label?: string;
  } | null;

  // Callback functions
  checkMicrophoneMuteStatus: (stream: MediaStream) => Promise<unknown>;
  handleCancel: () => Promise<void>;
  setStream: (stream: MediaStreamTrack | null) => void;
  setRetryCount: (count: number) => void;
  dispatch: any; // Redux dispatch function

  // Redux actions
  setRecordMimeType: (mimeType: string) => any;
  setMediaRecorder: (recorder: MediaRecorder) => any;
  setRecordingDevice: (device: any) => any;

  // Configuration options
  audioBitsPerSecond: number;
  retryCount: number;
  hasBackendReceivedBytesRef?: React.MutableRefObject<boolean>; // For original CreateNoteModal mute detection
  captureRecordingIdRef?: React.MutableRefObject<any>; // For GCS version mute detection
}

export const initiateMediaRecorder = async (
  config: MediaRecorderConfig,
  withHeadphones = false,
  isDeviceChange = false,
): Promise<MediaRecorder | null> => {
  const {
    recordingDevice,
    audioContextRef,
    destinationRef,
    currentAudioSourceRef,
    displayAudioSourceRef,
    checkMicrophoneMuteStatus,
    handleCancel,
    setStream,
    setRetryCount,
    dispatch,
    setRecordMimeType,
    setMediaRecorder,
    setRecordingDevice,
    audioBitsPerSecond,
    retryCount,
    hasBackendReceivedBytesRef,
    captureRecordingIdRef,
  } = config;

  // Create cleanup function using the shared utility
  const cleanupAudioResources = createCleanupAudioResources({
    audioContextRef,
    destinationRef,
    currentAudioSourceRef,
    displayAudioSourceRef,
  });

  console.log(
    `[MEDIA_RECORDER] Initiating MediaRecorder - withHeadphones: ${withHeadphones}, isDeviceChange: ${isDeviceChange}`,
  );

  try {
    // Close existing AudioContext if it exists (for device changes)
    if (audioContextRef.current && audioContextRef.current.state !== "closed") {
      await audioContextRef.current.close();
    }

    // Create AudioContext and destination once at the beginning
    const audioContext = new AudioContext();
    audioContextRef.current = audioContext;
    const destination = audioContext.createMediaStreamDestination();
    destinationRef.current = destination;

    let stream: MediaStream;
    let streamLabel: string;

    if (withHeadphones) {
      console.log(`[MEDIA_RECORDER] Setting up headphones recording mode`);

      const micStream = await navigator.mediaDevices.getUserMedia({
        audio: true,
      });

      console.log(
        `[MEDIA_RECORDER] Microphone stream obtained - tracks: ${micStream.getAudioTracks().length}, active: ${micStream.active}`,
      );

      // Check microphone status for headphone recording
      await checkMicrophoneMuteStatus(micStream);

      try {
        const displayStream = await navigator.mediaDevices.getDisplayMedia({
          audio: true,
        });

        console.log(
          `[MEDIA_RECORDER] Display stream obtained - tracks: ${displayStream.getAudioTracks().length}, active: ${displayStream.active}`,
        );

        const displaySource = audioContext.createMediaStreamSource(displayStream);
        const micSource = audioContext.createMediaStreamSource(micStream);

        // Track both sources for later disconnection and cleanup
        currentAudioSourceRef.current = micSource;
        if (displayAudioSourceRef) {
          displayAudioSourceRef.current = displaySource;
        }

        micSource.connect(destination);
        displaySource.connect(destination);

        // Use the combined stream
        stream = destination.stream;
        streamLabel = micStream.getAudioTracks()[0].label;
        console.log(
          `[MEDIA_RECORDER] Combined stream created - tracks: ${stream.getAudioTracks().length}, active: ${stream.active}`,
        );
      } catch (error) {
        console.error(`[MEDIA_RECORDER] Failed to get display media:`, error);
        throw new Error(
          "Please be sure to select the browser tab of your telehealth session. If you've already selected the tab, refresh the page and try again.",
        );
      }
    } else {
      console.log(`[MEDIA_RECORDER] Setting up standard recording mode - deviceId: ${recordingDevice?.deviceId}`);

      const initialStream = await navigator.mediaDevices.getUserMedia({
        audio: {
          deviceId: recordingDevice?.deviceId ? { exact: recordingDevice.deviceId } : undefined,
          echoCancellation: false,
        },
        video: false,
      });

      console.log(
        `[MEDIA_RECORDER] Standard stream obtained - tracks: ${initialStream.getAudioTracks().length}, active: ${initialStream.active}`,
      );

      // Check microphone status for regular recording
      await checkMicrophoneMuteStatus(initialStream);

      const source = audioContext.createMediaStreamSource(initialStream);

      // Track current source for later disconnection
      currentAudioSourceRef.current = source;

      source.connect(destination);
      stream = destination.stream;
      streamLabel = initialStream.getAudioTracks()[0].label;
    }

    // Verify that we have an active audio stream
    if (
      stream?.active &&
      stream.getAudioTracks().length > 0 &&
      stream.getAudioTracks().some((track) => track.enabled)
    ) {
      const audioTracks = stream.getAudioTracks();
      console.log(
        `[MEDIA_RECORDER] Stream validation passed - active: ${stream.active}, audio_tracks: ${audioTracks.length}, enabled_tracks: ${audioTracks.filter((t) => t.enabled).length}`,
      );

      const audioStream = stream.getAudioTracks()[0];
      setStream(audioStream);

      const audioStreamMuteHandler = async () => {
        if (!isIOS()) {
          // This only affects iOS so far.
          return;
        }

        // Check if backend has received bytes (for original CreateNoteModal)
        if (hasBackendReceivedBytesRef?.current) {
          // We've received bytes, so we can continue as usual.
          return;
        }

        // Check if GCS has partial upload available (for GCS version)
        if (captureRecordingIdRef?.current) {
          if (
            resumableUploadManager.hasPartialUploadAvailable(Number.parseInt(captureRecordingIdRef.current.toString()))
          ) {
            // We've sent bytes to GCS, so we can continue as usual.
            return;
          }
        }

        audioStream.stop();
        // Note: mediaRecorder is not available in this scope, so we rely on external cleanup
        cleanupAudioResources();
        Swal.fire({
          icon: "error",
          title: "Recording Setup Issue",
          text: "Recording while taking a phone call is not supported on iOS. Please stop the call to record or unmute your microphone.",
        });

        await handleCancel();
        audioStream.removeEventListener("mute", audioStreamMuteHandler);
        return null;
      };
      audioStream.addEventListener("mute", audioStreamMuteHandler);

      const recordMimeType = determineBestAudioFormat();
      dispatch(setRecordMimeType(recordMimeType));

      const options = {
        mimeType: recordMimeType,
        audioBitsPerSecond,
      };

      console.log(`[MEDIA_RECORDER] Creating MediaRecorder with options:`, options);

      const newMediaRecorder = new MediaRecorder(stream, options);

      console.log(
        `[MEDIA_RECORDER] MediaRecorder created successfully - state: ${newMediaRecorder.state}, mimeType: ${newMediaRecorder.mimeType}`,
      );

      dispatch(setMediaRecorder(newMediaRecorder));
      const track = stream.getAudioTracks()[0];
      const settings = track.getSettings();

      console.log(`[MEDIA_RECORDER] Audio track settings:`, settings);

      dispatch(
        setRecordingDevice({
          groupId: settings.groupId,
          deviceId: streamLabel,
          label: streamLabel,
        }),
      );
      setRetryCount(0);

      console.log(`[MEDIA_RECORDER] MediaRecorder initialization complete`);
      return newMediaRecorder;
    }

    const errorMsg = "No active audio stream detected. Please check your audio settings and try again.";
    console.error(`[MEDIA_RECORDER] Stream validation failed - ${errorMsg}`);
    throw new Error(errorMsg);
  } catch (err) {
    const error = err as Error;
    console.error(`[MEDIA_RECORDER] Error in initiateMediaRecorder:`, error);

    // See here for list of errors: https://developer.mozilla.org/en-US/docs/Web/API/MediaDevices/getUserMedia#exceptions
    const recoverableErrors = [
      "AbortError",
      "OverconstrainedError",
      "NotReadableError",
      "NotFoundError",
      "InvalidStateError",
    ];

    if (recoverableErrors.includes(error.name)) {
      console.log(`[MEDIA_RECORDER] Recoverable error encountered: ${error.name} - ${error.message}`);
      // This means the currently defined recordingDevice is no longer valid
      if (isDeviceChange && retryCount < 4) {
        console.log(`[MEDIA_RECORDER] Attempting retry ${retryCount + 1}/4 for device change`);
        // Reset state and useEffect will try to reinitialize
        // Only handle if this is a device change
        // Start of recording requires more configuration that isn't currently gracefully handled.
        // For example, doing this on initialization means timer won't start.
        setRetryCount(retryCount + 1);
        dispatch(setRecordingDevice(null));
        return null;
      } else {
        console.error(`[MEDIA_RECORDER] Max retries exceeded or not a device change - showing error to user`);
        // This will continue and throw an error, user can restart and new recording
        // will work as expected.
        Sentry.captureException(err, {
          extra: {
            deviceId: recordingDevice?.deviceId,
            label: recordingDevice?.label,
            retryCount: retryCount,
            withHeadphones: withHeadphones,
            isDeviceChange: isDeviceChange,
          },
        });
        setRetryCount(0);
      }
    }

    let errorMessage;

    if (error.name === "NotAllowedError") {
      errorMessage =
        "Microphone access was not granted. To record, please allow access to your microphone in your browser's site settings and try again.";
    } else if (error.name === "NotFoundError") {
      errorMessage =
        "No microphone was found. Please ensure your microphone is connected and enabled in your system settings.";
    } else if (error.name === "NotReadableError") {
      errorMessage =
        "Could not read from your microphone. It might be in use by another application or there could be a hardware issue.";
    } else if (error.message.includes("No active audio stream")) {
      errorMessage = "No audio was detected from your microphone. Please check your audio source and browser settings.";
    } else {
      errorMessage = `We detected a problem with your recording setup: ${error.message}`;
      Sentry.captureException(error, {
        extra: {
          deviceId: recordingDevice?.deviceId,
          label: recordingDevice?.label,
          withHeadphones: withHeadphones,
          isDeviceChange: isDeviceChange,
        },
      });
    }

    console.error(`[MEDIA_RECORDER] Showing error to user: ${errorMessage}`);
    Swal.fire({
      icon: "error",
      title: "Recording Setup Issue",
      text: errorMessage,
    });

    if (!isDeviceChange) {
      await handleCancel();
    }
    return null;
  }
};

export interface StartRecordingConfig {
  // Recording configuration
  recorderTimesliceMs: number;
  withHeadphones: boolean;
  isResume?: boolean; // Flag to indicate if this is resuming an existing recording

  // State refs
  captureRecordingIdRef: React.MutableRefObject<any>;
  recordingDevice?: {
    deviceId?: string;
    groupId?: string;
    label?: string;
  } | null;

  // Callback functions
  getCaptureDetails: () => object;
  setRecordingTime: (time: number) => void;
  displayRecordingTimer: () => void;
  dispatch: any; // Redux dispatch function

  // Redux actions
  setIsRecording: (recording: boolean) => any;
  setIsPaused: (paused: boolean) => any;

  // MediaRecorder initialization function
  initiateMediaRecorder: (withHeadphones: boolean) => Promise<MediaRecorder | null>;
}

export const handleStartRecording = async (config: StartRecordingConfig): Promise<void> => {
  const {
    recorderTimesliceMs,
    withHeadphones,
    isResume = false,
    captureRecordingIdRef,
    recordingDevice,
    getCaptureDetails,
    setRecordingTime,
    displayRecordingTimer,
    dispatch,
    setIsRecording,
    setIsPaused,
    initiateMediaRecorder,
  } = config;

  console.log(
    `[RECORDING] Starting recording process${isResume ? " (RESUME)" : ""} - audio_id: ${captureRecordingIdRef.current}`,
  );

  trackEvent(isResume ? EventType.RESUME_BUTTON : EventType.START_BUTTON, getCaptureDetails());
  await requestWakeLock();

  try {
    console.log(`[RECORDING] Initiating MediaRecorder setup`);
    const newMediaRecorder = await initiateMediaRecorder(withHeadphones);

    if (newMediaRecorder) {
      console.log(`[RECORDING] MediaRecorder obtained, starting recording with timeslice: ${recorderTimesliceMs}ms`);

      // Verify recorder is in correct state before starting
      if (newMediaRecorder.state !== "inactive") {
        console.warn(`[RECORDING] MediaRecorder not in inactive state: ${newMediaRecorder.state}`);
      }

      newMediaRecorder.start(recorderTimesliceMs);

      console.log(`[RECORDING] MediaRecorder.start() called - new state: ${newMediaRecorder.state}`);

      dispatch(setIsRecording(true));
      dispatch(setIsPaused(false));

      // Only reset recording time for NEW recordings, not for resumes
      if (!isResume) {
        setRecordingTime(0);
      }

      displayRecordingTimer();

      console.log(`[RECORDING] Recording state updated and timer started`);
    } else {
      console.error(`[RECORDING] Failed to get MediaRecorder from initiateMediaRecorder`);
    }
  } catch (error) {
    console.error(`[RECORDING] Error in handleStartRecording:`, error);
    Sentry.captureException(error, {
      extra: {
        audio_id: captureRecordingIdRef.current,
        withHeadphones: withHeadphones,
        recordingDevice: recordingDevice,
      },
    });
  }
};

export interface DeviceChangeConfig extends AudioContextRefs {
  // Audio device configuration
  recordingDevice?: {
    deviceId?: string;
    groupId?: string;
    label?: string;
  } | null;

  // MediaRecorder instance
  mediaRecorder: MediaRecorder | null;

  // Recording state
  withHeadphones: boolean;
  isPaused: boolean;
  recorderTimesliceMs: number;

  // Callback functions
  checkMicrophoneMuteStatus: (stream: MediaStream) => Promise<unknown>;
  setStream: (stream: MediaStreamTrack | null) => void;
  setRetryCount: (count: number) => void;
  dispatch: any; // Redux dispatch function

  // Redux actions
  setRecordingDevice: (device: any) => any;
}

export interface DeviceChangeResult {
  success: boolean;
  audioTrack?: MediaStreamTrack;
  deviceInfo?: {
    groupId?: string;
    deviceId?: string;
    label: string;
  };
}

export const handleDeviceChange = async (config: DeviceChangeConfig): Promise<DeviceChangeResult> => {
  const {
    recordingDevice,
    audioContextRef,
    destinationRef,
    currentAudioSourceRef,
    mediaRecorder,
    withHeadphones,
    isPaused,
    recorderTimesliceMs,
    checkMicrophoneMuteStatus,
    setStream,
    setRetryCount,
    dispatch,
    setRecordingDevice,
  } = config;

  try {
    // Validate required dependencies
    if (!audioContextRef.current || !destinationRef.current || !mediaRecorder) {
      console.error("[DEVICE_CHANGE] AudioContext, destination, or mediaRecorder not available for device change");
      return { success: false };
    }

    // Get new stream for the new device
    let newStream: MediaStream;

    if (withHeadphones) {
      console.log(`[DEVICE_CHANGE] Setting up headphones recording mode for new device`);

      const micStream = await navigator.mediaDevices.getUserMedia({
        audio: true,
      });

      await checkMicrophoneMuteStatus(micStream);

      // Disconnect old source
      if (currentAudioSourceRef.current) {
        currentAudioSourceRef.current.disconnect();
      }

      const micSource = audioContextRef.current.createMediaStreamSource(micStream);

      // Track new source
      currentAudioSourceRef.current = micSource;

      // Connect new source to existing destination
      micSource.connect(destinationRef.current);

      newStream = destinationRef.current.stream;
    } else {
      console.log(`[DEVICE_CHANGE] Setting up standard recording mode for device: ${recordingDevice?.deviceId}`);

      newStream = await navigator.mediaDevices.getUserMedia({
        audio: {
          deviceId: recordingDevice?.deviceId ? { exact: recordingDevice.deviceId } : undefined,
          echoCancellation: false,
        },
      });

      await checkMicrophoneMuteStatus(newStream);

      // Disconnect old source
      if (currentAudioSourceRef.current) {
        currentAudioSourceRef.current.disconnect();
      }

      // Create new source and connect to existing destination
      const newSource = audioContextRef.current.createMediaStreamSource(newStream);
      currentAudioSourceRef.current = newSource;
      newSource.connect(destinationRef.current);
    }

    // Update stream state
    const audioTracks = newStream.getAudioTracks();
    if (audioTracks.length > 0) {
      const audioStream = audioTracks[0];
      setStream(audioStream);

      const settings = audioStream.getSettings();
      const deviceInfo = {
        groupId: settings.groupId,
        deviceId: settings.deviceId,
        label: audioStream.label,
      };

      dispatch(setRecordingDevice(deviceInfo));

      console.log(`[DEVICE_CHANGE] Successfully switched to new device: ${audioStream.label}`);

      // Resume recording if it was active and not paused
      if (mediaRecorder.state === "inactive" && !isPaused) {
        mediaRecorder.start(recorderTimesliceMs);
        console.log(`[DEVICE_CHANGE] Resumed recording with new device`);
      }

      setRetryCount(0);

      return {
        success: true,
        audioTrack: audioStream,
        deviceInfo,
      };
    }

    return { success: false };
  } catch (error) {
    console.error("[DEVICE_CHANGE] Error in switching to new device: ", error);
    return { success: false };
  }
};

/**
 * Fetches available audio input devices from the browser
 * @returns Promise resolving to array of audio input devices with deviceId, groupId, and label
 */
export const fetchAudioInputDevices = async () => {
  const devices = await window.navigator.mediaDevices.enumerateDevices();
  const audioDevices = devices
    .filter((device) => device.kind === "audioinput")
    .map((device) => ({
      deviceId: device.deviceId,
      groupId: device.groupId,
      label: device.label,
    }));
  return audioDevices;
};

/**
 * Creates a device change handler function that responds to system audio device changes
 * This is meant to be used with navigator.mediaDevices.addEventListener('devicechange', handler)
 *
 * The handler will:
 * - Skip if headphones mode is enabled
 * - Fetch updated list of audio devices
 * - Check if current recording device is still available
 * - Auto-switch to first available device if current device is unavailable
 *
 * @param withHeadphones Whether headphones recording mode is enabled
 * @param isRecording Whether recording is currently active
 * @param isPaused Whether recording is currently paused
 * @param recordingDevice Current recording device configuration
 * @param mediaRecorder Current MediaRecorder instance
 * @param dispatch Redux dispatch function
 * @param setAudioDevices Redux action to set audio devices
 * @param setRecordingDevice Redux action to set recording device
 * @returns Async function to handle device change events
 */
export const createDeviceChangeHandler = (
  withHeadphones: boolean,
  isRecording: boolean,
  isPaused: boolean,
  recordingDevice:
    | {
        deviceId?: string;
        groupId?: string;
        label?: string;
      }
    | null
    | undefined,
  mediaRecorder: MediaRecorder | null,
  dispatch: any,
  setAudioDevices: (devices: any[]) => any,
  setRecordingDevice: (device: any) => any,
) => {
  return async () => {
    if (withHeadphones) {
      return;
    }

    try {
      const devices = await fetchAudioInputDevices();
      dispatch(setAudioDevices(devices));

      // Check if current device is still available
      const currentDevice = devices.find(
        (device) => device.deviceId === recordingDevice?.deviceId && device.groupId === recordingDevice?.groupId,
      );
      if (
        (isRecording || isPaused) &&
        ((!currentDevice && mediaRecorder?.state !== "inactive") ||
          (!mediaRecorder?.stream?.active && mediaRecorder?.state !== "inactive"))
      ) {
        console.log("firing from onchange handler");
        console.log(`setting device ${devices[0].groupId} ${devices[0].deviceId} ${devices[0].label}`);
        dispatch(setRecordingDevice(devices[0]));
      }
    } catch (error) {
      console.error("Error switching devices:", error);
    }
  };
};
